import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';

import { CreateAccountFormValue } from './CreateAccountFormContent';

interface Context {
  shouldCreateSelection?: boolean;
  accountRequestId?: string;
  products?: string[];
  deliveryWindows?: string[];
  hasWishlistedProducts: boolean;
  formDefaultValues?: Partial<CreateAccountFormValue>;
  createSource: 'lookbook' | 'lookbook-request' | 'listing';
}

export const useCreateAccountModalContext = (): Context => {
  const context = React.useContext(CreateAccountModalContext);
  if (context === null) {
    throw new Error('CreateAccountModalContext can not be used outside the scope of CreateAccountModalContextProvider');
  }

  return context;
};

const CreateAccountModalContext = React.createContext<Context | null>(null);

interface Props {
  shouldCreateSelection: boolean;
  products?: string[];
  deliveryWindows?: string[];
  accountRequestId?: string;
  formDefaultValues?: Partial<CreateAccountFormValue>;
  createSource: 'lookbook' | 'lookbook-request' | 'listing';
}

export const CreateAccountModalContextProvider = ({
  children,
  products,
  deliveryWindows,
  accountRequestId,
  formDefaultValues,
  shouldCreateSelection,
  createSource,
}: React.WithChildren<Props>) => {
  const hasWishlistedProducts = React.useMemo(
    () => isDefined(products) && !isEmpty(products) && isDefined(deliveryWindows) && !isEmpty(deliveryWindows),
    [products, deliveryWindows],
  );

  return (
    <CreateAccountModalContext.Provider
      value={{
        accountRequestId,
        createSource,
        deliveryWindows,
        formDefaultValues,
        hasWishlistedProducts,
        products,
        shouldCreateSelection,
      }}
    >
      {children}
    </CreateAccountModalContext.Provider>
  );
};
